import { memo, useMemo, useRef } from "react";

import styles from "./ShipmentBox.module.css";

import { FbaBox, FbaShipment, getFbaBatchItemById, getPackingGroupItem, useShipmentContext } from "@/contexts/ShipmentContext";
import { usePrinterContext } from "@/contexts/PrinterContext";
import useAccordion from "@/hooks/useAccordion";
import Flex from "@/components/Flex/Flex";
import Button from "@/components/forms/Button";
import IconArrow, { Direction } from "@/components/icons/IconArrow";
import IconBox from "@/components/icons/IconBox";
import IconPrintGoogle from "@/components/icons/IconPrintGoogle";

import ProductView from "../ProductView";
import { useUserContext } from "@/contexts/UserContext";
import { BoxContentsProvider } from "@boxem/core-api-schema";

const UPSTrackingNumberMagic = (box: FbaBox) => {
  // for other partners, we may need to figure something else out...
  return `https://www.ups.com/track?loc=en_US&tracknum=${box.trackingId}&requester=WT/trackdetails\\`;
}


export type BoxProps = {
  box: FbaBox;
  shipment?: FbaShipment;
};

export default function Box({ box, shipment }: BoxProps) {
  const { openPrintFNSKULabelModal } = usePrinterContext();
  const { currentBatch } = useShipmentContext();
  const boxItemsRef = useRef(null);
  const { collapsed, toggleCollapsed } = useAccordion(boxItemsRef, false, "0");
  const isOpen = !collapsed;

  return (
    <div className={styles.box}>
      <div className={styles.boxHeaderWrapper}>
        <BoxHeader
          box={box}
          isOpen={isOpen}
          onCollapseClicked={toggleCollapsed}
          shipment={shipment}
        />
      </div>
      <div className={styles.boxItems} ref={boxItemsRef}>
        {box.packingItems.map((item, i) => {
          const batchItem = getFbaBatchItemById(currentBatch, item.batchItemId);
          const packingGroupItem = getPackingGroupItem(currentBatch, box.packingGroupId, batchItem.sku);
          return (
            <div className={styles.boxItem} key={i}>
              <ProductView
                item={batchItem}
                quantity={item.quantity}
                content1={
                  <Button
                    type="secondary"
                    size="small"
                    style={{flex: "0", alignSelf: "flex-start"}}
                    onClick={() => openPrintFNSKULabelModal({
                      fnsku: packingGroupItem.fnsku,
                      productName: batchItem.name,
                      expirationDate: batchItem.expirationDate,
                      quantity: item.quantity,
                      productCondition: batchItem.condition,
                    })}
                  >
                    <IconPrintGoogle />
                    Print FNSKU
                  </Button>
                }
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface BoxHeaderProps extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  box: FbaBox;
  shipment?: FbaShipment;
  onCollapseClicked?: () => void;
  isOpen?: boolean;
}

const BoxIconComponent = memo(function(props) {
  return (
    <div className={`${styles.boxIcon} allBoxesForDragNDrop`} style={{position: "relative" }}>
      <svg xmlns="http://www.w3.org/2000/svg" width="35" height="31" viewBox="0 0 35 31" fill="none">
        <path d="M24.0386 1.112C23.9293 1.03933 23.8 1 23.6686 1H10.3346C10.2033 1 10.074 1.03933 9.96463 1.112L1.66797 7C1.66797 7 2.0413 7.66667 2.33464 7.66667H31.668C31.9613 7.66667 32.3346 7 32.3346 7L24.0386 1.112Z" fill="#C2C2C2"/>
        <path d="M1.66797 7H32.3346V21C32.3346 25.4183 28.7529 29 24.3346 29H9.66796C5.24968 29 1.66797 25.4183 1.66797 21V7Z" fill="#D6D6D6"/>
        <path d="M13 7L15 1H19L21 7H13Z" fill="#DA3109"/>
        <path d="M21 7H13V15.6667H21V7Z" fill="#FF5823"/>
      </svg>
      <img width="35" height="31" src="/images/box-anim2.svg" style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-52%, -66%)", opacity: 0.0 }}/>
      <img width="45" height="39" src="/images/box-anim3.svg" style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-52%, -70%)", opacity: 0.0 }}/>
      <img width="56" height="43" src="/images/box-anim4.svg" style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -66%)", opacity: 0.0 }}/>
    </div>
  );
});

export function BoxHeader({
  box,
  shipment,
  onCollapseClicked,
  isOpen = true,
  className,
  ...rest
}: BoxHeaderProps) {
  const { currentBatch } = useShipmentContext();
  const { printFBABoxLabels } = usePrinterContext();
  const { addressBook } = useUserContext();
  const fmtFloat = (f: number) => f.toLocaleString(navigator.language, { minimumFractionDigits: 0, maximumFractionDigits: 3 });
  const onPrintFBABoxLabel = !shipment ? null : () => {
    const fromAddress = addressBook.find(a => a.id === shipment.fromId);
    printFBABoxLabels(
      shipment,
      fromAddress,
      [box],
      currentBatch.boxContentsProvider === BoxContentsProvider.Barcode2D,
      currentBatch,
    );
  }
  const packingGroupIndex = useMemo(() => (
    currentBatch.chosenPackingOption.packingGroups.findIndex(p => p.inboundPackingGroupId === box.packingGroupId) + 1
  ), [box.packingGroupId]);
  return (
    <div className={`${styles.boxHeader} ${className ?? ""}`} {...rest}>
      <div className={styles.boxLabel}>
        <BoxIconComponent/>
        <Flex direction="column" style={{ width: 0, flex: 1 }}>
          <div style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
            <span className="strong">Box {box.displayIndex}&nbsp;</span>
            <span className="x-small-text weak"> | Pack Group {packingGroupIndex}</span>
            {box.trackingId && <span className="x-small-text weak"> | Tracking Number:&nbsp;
              <a target="_blank" href={UPSTrackingNumberMagic(box)} onClick={e => e.stopPropagation()}>{box.trackingId}</a>
            </span>}
          </div>
          <div style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
            <span className="small-text weak">{`${fmtFloat(box.length)} x ${fmtFloat(box.width)} x ${fmtFloat(box.height)} in`}</span>
            <span className="small-text weak">&nbsp;•&nbsp;</span>
            <span className="small-text weak">{`${fmtFloat(box.weight)} lbs`}</span>
          </div>
        </Flex>
      </div>
      {onPrintFBABoxLabel && (
        <Button size="small" type="secondary" style={{alignSelf: "center"}} onClick={onPrintFBABoxLabel}>
          <IconPrintGoogle />
          &nbsp;Print Box Label
        </Button>
      )}
      {onCollapseClicked && <Button type="text"
        onClick={onCollapseClicked}
      >
        <IconArrow size={24} direction={isOpen ? Direction.Up : Direction.Down} style={{ transition: "transform 200ms" }} />
      </Button>}
    </div>
  );
}
